import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Chip, Button, TextField, InputAdornment, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
} from '@mui/material';
import { Search as SearchIcon, RestoreFromTrash, DeleteForever } from '@mui/icons-material';
import { businessAPI } from '../api';
import { TOUCH } from '../theme/tokens';

// Nothing records when an item was deleted, but nothing edits a deleted item either, so its
// last-changed time is the deletion time in practice.
const deletedAround = (iso) => (iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null);

/**
 * Settings → Deleted. Deleted customers and products are only hidden, so they are listed here
 * with a Restore button. Plans are left out: they have no history of their own to bring back.
 *
 * Restoring a product warns first when customers still have a running plan on it — products
 * deleted before the July safety check kept those plans, and bringing the product back starts
 * billing them again.
 */
export default function DeletedItemsPage({ businessId, areas = [], onRestored, canDeleteForever = false }) {
    const [kind, setKind] = useState('customers');   // 'customers' | 'products'
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const [search, setSearch] = useState('');
    // Restore confirmation: { kind, item, holders (products only, null while loading) }
    const [confirm, setConfirm] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [confirmError, setConfirmError] = useState('');
    // Delete forever (owner only): { item, preview (null while loading), agreed, busy, error }
    const [forever, setForever] = useState(null);

    const load = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError('');
        try {
            const [cRes, pRes] = await Promise.all([
                businessAPI.getCustomers(businessId, true),
                businessAPI.getBusinessProducts(businessId, false, true),
            ]);
            const byNewest = (a, b) => (b.f_updated_at || '').localeCompare(a.f_updated_at || '');
            setCustomers((cRes.data || []).filter(c => c.f_is_archived).sort(byNewest));
            setProducts((pRes.data || []).filter(p => p.f_is_archived && !p.f_parent_business_product_id).sort(byNewest));
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not load deleted items.');
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => { load(); }, [load]);

    const openConfirm = async (k, item) => {
        setConfirmError('');
        setConfirm({ kind: k, item, holders: k === 'products' ? null : [] });
        if (k === 'products') {
            try {
                const res = await businessAPI.getDeletedProductPlanHolders(item.f_id);
                setConfirm(c => (c && c.item.f_id === item.f_id ? { ...c, holders: res.data?.customers || [] } : c));
            } catch {
                setConfirm(c => (c ? { ...c, holders: [] } : c));
            }
        }
    };

    const doRestore = async () => {
        const { kind: k, item } = confirm;
        setRestoring(true);
        setConfirmError('');
        try {
            if (k === 'customers') await businessAPI.restoreCustomer(item.f_id);
            else await businessAPI.restoreProduct(item.f_id);
            if (k === 'customers') setCustomers(list => list.filter(c => c.f_id !== item.f_id));
            else setProducts(list => list.filter(p => p.f_id !== item.f_id));
            setDone(`${item.f_name} is back.`);
            setConfirm(null);
            onRestored && onRestored(k);
        } catch (e) {
            const d = e.response?.data?.detail;
            setConfirmError(typeof d === 'string' ? d : 'Could not restore. Please try again.');
        } finally {
            setRestoring(false);
        }
    };

    const openForever = async (item) => {
        setForever({ item, preview: null, agreed: false, busy: false, error: '' });
        try {
            const res = await businessAPI.getDeleteForeverPreview(item.f_id);
            setForever(f => (f && f.item.f_id === item.f_id ? { ...f, preview: res.data } : f));
        } catch (e) {
            const d = e.response?.data?.detail;
            setForever(f => (f ? { ...f, preview: {}, error: typeof d === 'string' ? d : 'Could not check this customer.' } : f));
        }
    };
    const doForever = async () => {
        const item = forever.item;
        setForever(f => ({ ...f, busy: true, error: '' }));
        try {
            await businessAPI.deleteCustomerForever(item.f_id);
            setCustomers(list => list.filter(c => c.f_id !== item.f_id));
            setDone(`${item.f_name} was deleted forever.`);
            setForever(null);
        } catch (e) {
            const d = e.response?.data?.detail;
            setForever(f => ({ ...f, busy: false, error: typeof d === 'string' ? d : 'Could not delete. Nothing was removed.' }));
        }
    };

    const q = search.trim().toLowerCase();
    const list = (kind === 'customers' ? customers : products).filter(x =>
        !q || (x.f_name || '').toLowerCase().includes(q) || (x.f_mobile || '').includes(q));
    const areaName = (id) => areas.find(a => a.f_id === id)?.f_name;

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Deleted customers and products are kept here. Tap <strong>Restore</strong> to bring one back.
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                {[
                    { key: 'customers', label: `Customers (${customers.length})` },
                    { key: 'products', label: `Products (${products.length})` },
                ].map(t => (
                    <Chip key={t.key} label={t.label} clickable
                        color={kind === t.key ? 'primary' : 'default'}
                        variant={kind === t.key ? 'filled' : 'outlined'}
                        onClick={() => { setKind(t.key); setSearch(''); }} />
                ))}
            </Box>

            {done && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setDone('')}>{done}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {(kind === 'customers' ? customers : products).length > 8 && (
                <TextField size="small" fullWidth placeholder={kind === 'customers' ? 'Search name or mobile' : 'Search product'}
                    value={search} onChange={e => setSearch(e.target.value)} sx={{ mb: 1.5 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : list.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <RestoreFromTrash sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {q ? 'No matches found' : kind === 'customers' ? 'No deleted customers' : 'No deleted products'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    {list.map(x => {
                        const when = deletedAround(x.f_updated_at);
                        const meta = kind === 'customers'
                            ? [x.f_mobile, areaName(x.f_area_id)].filter(Boolean).join(' · ')
                            : [x.current_price != null ? `₹${x.current_price}` : null,
                               x.f_billing_type === 'MonthlyFlatFee' ? 'Monthly rate' : 'Per item'].filter(Boolean).join(' · ');
                        const onPlan = kind === 'products' ? (x.active_customer_count || 0) : 0;
                        return (
                            <Box key={x.f_id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                                minHeight: TOUCH.min, borderBottom: 1, borderColor: 'divider',
                                '&:last-child': { borderBottom: 0 },
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>{x.f_name}</Typography>
                                    {meta && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{meta}</Typography>}
                                    {when && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>Deleted around {when}</Typography>}
                                    {onPlan > 0 && (
                                        <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ display: 'block' }}>
                                            {onPlan} customer{onPlan !== 1 ? 's' : ''} still on its plan
                                        </Typography>
                                    )}
                                </Box>
                                {/* Same style and size, side by side; Delete forever only differs in colour */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                    <Button size="small" variant="outlined" startIcon={<RestoreFromTrash />}
                                        onClick={() => openConfirm(kind, x)}
                                        sx={{ minHeight: TOUCH.dense, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        Restore
                                    </Button>
                                    {kind === 'customers' && canDeleteForever && (
                                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteForever />}
                                            onClick={() => openForever(x)}
                                            sx={{ minHeight: TOUCH.dense, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                            Delete forever
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Dialog open={!!confirm} onClose={restoring ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Restore {confirm?.item.f_name}?</DialogTitle>
                <DialogContent>
                    {confirmError && <Alert severity="error" sx={{ mb: 1.5 }}>{confirmError}</Alert>}
                    {confirm?.kind === 'customers' && (
                        <Typography variant="body2">
                            They will show in your customer list again, <strong>without any plans</strong>.
                            Add their plans again if they still take deliveries.
                        </Typography>
                    )}
                    {confirm?.kind === 'products' && (confirm.holders === null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Checking customers…</Typography>
                        </Box>
                    ) : confirm.holders.length === 0 ? (
                        <Typography variant="body2">It will show in your products list again.</Typography>
                    ) : (
                        <>
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                {confirm.holders.length} customer{confirm.holders.length !== 1 ? 's' : ''} still
                                {confirm.holders.length !== 1 ? ' have' : ' has'} a plan on this product.
                                After restoring, they will be <strong>billed for it again</strong> from the next bill.
                            </Alert>
                            {confirm.holders.slice(0, 10).map(h => (
                                <Typography key={h.f_id} variant="body2">• {h.f_name} <Typography component="span" variant="caption" color="text.secondary">{h.f_mobile}</Typography></Typography>
                            ))}
                            {confirm.holders.length > 10 && (
                                <Typography variant="caption" color="text.secondary">and {confirm.holders.length - 10} more</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                To stop a plan, open that customer after restoring.
                            </Typography>
                        </>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={restoring}>Cancel</Button>
                    <Button variant="contained" onClick={doRestore}
                        disabled={restoring || (confirm?.kind === 'products' && confirm.holders === null)}>
                        {restoring ? <CircularProgress size={20} color="inherit" /> : 'Restore'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete forever: real numbers of what is lost, and a tick to say "I understand" —
                one deliberate step, simpler than typing a word on a phone */}
            <Dialog open={!!forever} onClose={forever?.busy ? undefined : () => setForever(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 800 }}>
                    Delete {forever?.item.f_name} forever?
                </DialogTitle>
                <DialogContent>
                    {forever?.error && <Alert severity="error" sx={{ mb: 1.5 }}>{forever.error}</Alert>}
                    {forever && forever.preview === null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Checking what will be lost…</Typography>
                        </Box>
                    ) : forever?.preview && (
                        <>
                            <Alert severity="error" icon={<DeleteForever />} sx={{ mb: 1.5 }}>
                                <strong>This cannot be undone.</strong> Everything for this customer will be lost:
                                their bills, payments, dues, orders, plans and notes. They will not come back in
                                Deleted, and old bills cannot be opened again.
                            </Alert>
                            {(() => {
                                const pv = forever.preview;
                                const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
                                const lines = [
                                    pv.bills ? `${pv.bills} bill${pv.bills !== 1 ? 's' : ''}` : null,
                                    pv.payments ? `${pv.payments} payment${pv.payments !== 1 ? 's' : ''} (${inr(pv.payments_total)})` : null,
                                    pv.orders ? `${pv.orders} order${pv.orders !== 1 ? 's' : ''}` : null,
                                    pv.plan_changes ? `${pv.plan_changes} plan change${pv.plan_changes !== 1 ? 's' : ''}` : null,
                                ].filter(Boolean);
                                return (
                                    <>
                                        {lines.length > 0 && (
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>Will be deleted: {lines.join(' · ')}</Typography>
                                        )}
                                        {pv.dues > 0.009 && (
                                            <Typography variant="body2" fontWeight={800} color="error.main" sx={{ mb: 1 }}>
                                                Dues of {inr(pv.dues)} still owed will be lost — you will not be able to collect them.
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                            <FormControlLabel sx={{ mt: 0.5, alignItems: 'flex-start' }}
                                control={<Checkbox color="error" checked={forever.agreed} sx={{ pt: 0.5 }}
                                    onChange={e => setForever(f => ({ ...f, agreed: e.target.checked }))} />}
                                label={<Typography variant="body2" fontWeight={700}>
                                    I understand everything for this customer will be lost
                                </Typography>} />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setForever(null)} disabled={forever?.busy}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={doForever}
                        disabled={!forever?.preview || forever?.busy || !forever?.agreed}>
                        {forever?.busy ? <CircularProgress size={20} color="inherit" /> : 'Delete forever'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
